import React from "react";
import { Link } from "react-router-dom";
import { Heart, Target, Eye, Sparkles, ArrowRight, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import { heroImages, stats, team, org } from "../mock";

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
    <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
    <div className="container-prc relative text-center max-w-3xl mx-auto">
      <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">{eyebrow}</Badge>
      <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">{title}</h1>
      <p className="mt-5 text-lg text-muted-foreground">{subtitle}</p>
    </div>
  </section>
);

const values = [
  { icon: Target, title: "Our Mission", text: "To create first connections for children and caregivers through enrichment, education, and community support." },
  { icon: Eye, title: "Our Vision", text: "A community where every family—regardless of means—has the resources and connection to thrive." },
  { icon: Sparkles, title: "Our Values", text: "Inclusion, compassion, playfulness, and unwavering respect for every family that walks through our doors." },
];

const About = () => {
  return (
    <div>
      <PageHero
        eyebrow="About Us"
        title={`"Where Early Childhood Leaders Connect, Grow, and Lead Forward."...`}
        subtitle={`Since 2001, Dallas Early Childhood Resource Institute (DECRI) has been on the front-lines as an advocate focused on strengthening families through resources, education, parent leadership, financial/economic security, and family development.`}
      />

      {/* STORY */}
      <section className="py-20">
        <div className="container-prc grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="img-zoom rounded-[2rem] overflow-hidden shadow-lift">
            <img src={heroImages.about} alt="Our story" className="w-full h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={100}>
            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-5">Our Story</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground leading-tight">Two decades of advocacy and impact</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Founded in 2001, Dallas Early Childhood Resource Institute (DECRI) began as a grassroots, community-based effort to stand alongside the people who shape a child's earliest years. What started small has grown into a trusted 501(c)(3) advocate for early childhood professionals and single parents across our community.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              For more than twenty years, we've stayed on the front lines—strengthening families through resources, education, parent leadership, financial and economic security, and family development—so that every child can grow up supported and every caregiver can lead with confidence.
            </p>
            <Button asChild className="btn-cta mt-8 bg-primary hover:bg-primary/90 rounded-full px-7 h-12 font-bold">
              <Link to="/programs">See Our Programs <ArrowRight className="h-5 w-5 ml-2" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 bg-secondary/40">
        <div className="container-prc">
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div className="card-hover bg-white rounded-2xl p-8 shadow-soft h-full">
                    <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-5"><Icon className="h-7 w-7" /></span>
                    <h3 className="font-display font-bold text-xl text-foreground">{v.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16">
        <div className="container-prc grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 70} className="text-center bg-white rounded-2xl py-8 shadow-soft">
              <p className="font-display font-extrabold text-3xl lg:text-4xl text-primary">{s.value}</p>
              <p className="text-sm text-muted-foreground mt-1 px-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TEAM */}
      <section className="py-20 bg-secondary/40">
        <div className="container-prc">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-4">Our People</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Meet the team behind the smiles</h2>
            <p className="mt-4 text-muted-foreground">A dedicated group of educators, coordinators, and caregivers who show up with heart every day.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <Reveal key={m.name} delay={i * 80}>
                <div className="card-hover bg-white rounded-2xl p-7 text-center shadow-soft">
                  <span className="grid place-items-center h-20 w-20 mx-auto rounded-full bg-primary/10 text-primary font-display font-extrabold text-2xl">{m.initials}</span>
                  <h3 className="font-display font-bold text-lg text-foreground mt-4">{m.name}</h3>
                  <p className="text-sm text-accent font-semibold mt-1">{m.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container-prc">
          <Reveal className="bg-primary rounded-[2rem] p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8 text-primary-foreground">
            <div className="max-w-xl">
              <h2 className="font-display font-extrabold text-3xl">Become part of our story</h2>
              <p className="mt-3 text-primary-foreground/85">Volunteer, donate, or join a program—there’s a place for you here.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-7 h-12"><Link to="/get-involved"><Users className="h-5 w-5 mr-2" /> Get Involved</Link></Button>
              <Button asChild variant="outline" className="btn-cta bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold rounded-full px-7 h-12"><Link to="/donate"><Heart className="h-5 w-5 mr-2" /> Donate</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
